import { type FormEvent } from 'react';
import {
  Avatar,
  Banner,
  Button,
  ButtonRouterLink,
  HandlePreview,
  PasswordField,
  PasswordStrengthMeter,
  SectionHead,
  Skeleton,
  TextField,
  Toast
} from '../components/primitives';
import { useAccountVM } from '../lib/vm/useAccountVM';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_SCORE,
  scorePassword
} from '../lib/passwordScore';
import './Account.css';

/**
 * `/account` page shell + Profile + Email + Password + Function role + Sessions
 * sections (turns 1.1–1.5).
 *
 * The remaining turn (1.6 Danger zone) extends this surface with one more
 * `<section>` block.
 */
export function Account() {
  const vm = useAccountVM();

  function handleProfileSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void vm.actions.saveProfile();
  }

  function handleEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void vm.actions.requestEmailChange();
  }

  function handlePasswordSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void vm.actions.changePassword();
  }

  function handleDeleteSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void vm.actions.deleteAccount();
  }

  function formatOrgNames(names: string[]): string {
    if (names.length <= 1) return names[0] ?? '';
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }

  const pwdScore = scorePassword(vm.newPassword);
  const pwdLongEnough = vm.newPassword.length >= PASSWORD_MIN_LENGTH;
  const pwdStrongEnough = pwdLongEnough && pwdScore >= PASSWORD_MIN_SCORE;
  const pwdsMismatch = !!vm.confirmPassword && vm.confirmPassword !== vm.newPassword;
  const passwordSubmitDisabled =
    vm.passwordStatus === 'submitting' ||
    !vm.currentPassword ||
    !pwdStrongEnough ||
    !vm.confirmPassword ||
    pwdsMismatch;

  const loading = vm.status === 'loading';
  const trimmedUsername = vm.username.trim();
  const previewName =
    trimmedUsername || vm.profile?.username || 'username';
  const discriminator = vm.profile?.discriminator ?? null;
  const handleString = vm.profile
    ? `@${vm.profile.username}#${String(vm.profile.discriminator).padStart(4, '0').slice(-4)}`
    : '';

  return (
    <section className="acc-page" data-testid="account-page">
      <h1 className="acc-h1">Account</h1>
      <p className="acc-lede">
        Your personal Penrose account. Org-scoped settings live under Settings.
      </p>

      <section className="acc-section" data-testid="account-section-profile">
        <SectionHead marker="01" title="Profile" tag="SEC.01 — IDENTITY" />

        {loading ? (
          <div className="acc-skeleton" data-testid="account-profile-skeleton">
            <Skeleton width="60%" height={20} />
            <Skeleton width="40%" height={14} />
            <Skeleton width="80%" height={14} />
          </div>
        ) : vm.status === 'error' || !vm.profile ? (
          <Banner kind="error" title="Couldn't load your profile">
            Refresh the page or sign out and back in.
          </Banner>
        ) : (
          <form className="acc-form" onSubmit={handleProfileSubmit} noValidate>
            <div className="acc-identity-row">
              <Avatar name={vm.displayName || vm.profile.username} size="md" />
              <div className="acc-identity-meta">
                <span className="acc-identity-handle" data-testid="account-handle">
                  {handleString}
                </span>
                <span className="acc-identity-hint">
                  Avatar uses your display-name initials.
                </span>
              </div>
            </div>

            <TextField
              id="acc-display-name"
              label="Display name"
              type="text"
              autoComplete="name"
              maxLength={80}
              value={vm.displayName}
              onChange={(e) => vm.actions.setDisplayName(e.currentTarget.value)}
            />

            <TextField
              id="acc-username"
              label="Username"
              type="text"
              autoComplete="username"
              maxLength={32}
              value={vm.username}
              onChange={(e) => vm.actions.setUsername(e.currentTarget.value)}
              helpText="Letters, numbers, underscores and dashes. The 4-digit suffix doesn't change when you rename."
              errorText={vm.profileError ?? undefined}
              trailing={
                <HandlePreview
                  username={previewName}
                  discriminator={discriminator}
                />
              }
            />

            <div className="acc-actions">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={!vm.dirty || vm.status === 'saving'}
                data-testid="account-profile-save"
              >
                {vm.status === 'saving' ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        )}
      </section>

      <section className="acc-section" data-testid="account-section-email">
        <SectionHead marker="02" title="Email" tag="SEC.02 — EMAIL" />

        <div className="acc-row" data-testid="account-email-row">
          <div className="acc-row-stack">
            <span className="acc-row-label">Current email</span>
            <span className="acc-row-value" data-testid="account-current-email">
              {vm.email ?? '—'}
            </span>
          </div>
          {vm.emailFormOpen ? null : (
            <Button
              type="button"
              variant="default"
              size="md"
              onClick={vm.actions.openEmailForm}
              data-testid="account-email-open"
            >
              Change email
            </Button>
          )}
        </div>

        {vm.emailFormOpen ? (
          <form
            className="acc-form"
            onSubmit={handleEmailSubmit}
            noValidate
            data-testid="account-email-form"
          >
            <TextField
              id="acc-new-email"
              label="New email"
              type="email"
              autoComplete="email"
              value={vm.newEmail}
              onChange={(e) => vm.actions.setNewEmail(e.currentTarget.value)}
              helpText="We'll email a confirmation link to the new address. Your email won't change until you click that link. Your current email gets a notification too."
              errorText={vm.emailError ?? undefined}
              disabled={vm.emailStatus === 'submitting'}
            />

            <div className="acc-actions">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={vm.emailStatus === 'submitting' || vm.emailStatus === 'sent'}
                data-testid="account-email-submit"
              >
                {vm.emailStatus === 'submitting'
                  ? 'Sending…'
                  : vm.emailStatus === 'sent'
                  ? 'Link sent'
                  : 'Send confirmation link'}
              </Button>
              <Button
                type="button"
                variant="default"
                size="md"
                onClick={vm.actions.cancelEmailForm}
                data-testid="account-email-cancel"
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </section>

      <section className="acc-section" data-testid="account-section-password">
        <SectionHead marker="03" title="Password" tag="SEC.03 — PASSWORD" />

        {vm.passwordFormOpen ? (
          <form
            className="acc-form"
            onSubmit={handlePasswordSubmit}
            noValidate
            data-testid="account-password-form"
          >
            <PasswordField
              id="acc-current-password"
              label="Current password"
              autoComplete="current-password"
              value={vm.currentPassword}
              onChange={(e) => vm.actions.setCurrentPassword(e.currentTarget.value)}
              disabled={vm.passwordStatus === 'submitting'}
              errorText={vm.passwordError ?? undefined}
            />

            <TextField
              id="acc-new-password"
              label="New password"
              type="password"
              autoComplete="new-password"
              value={vm.newPassword}
              onChange={(e) => vm.actions.setNewPassword(e.currentTarget.value)}
              disabled={vm.passwordStatus === 'submitting'}
              helpText={`Minimum ${PASSWORD_MIN_LENGTH} characters. Unlocks at "Fair" or stronger.`}
              trailing={
                <PasswordStrengthMeter
                  score={pwdScore}
                  hasInput={vm.newPassword.length > 0}
                />
              }
            />

            <TextField
              id="acc-confirm-password"
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={vm.confirmPassword}
              onChange={(e) => vm.actions.setConfirmPassword(e.currentTarget.value)}
              disabled={vm.passwordStatus === 'submitting'}
              errorText={pwdsMismatch ? "Passwords don't match." : undefined}
            />

            <div className="acc-actions">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={passwordSubmitDisabled}
                data-testid="account-password-submit"
              >
                {vm.passwordStatus === 'submitting' ? 'Updating…' : 'Update password'}
              </Button>
              <Button
                type="button"
                variant="default"
                size="md"
                onClick={vm.actions.cancelPasswordForm}
                data-testid="account-password-cancel"
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="acc-row" data-testid="account-password-row">
            <div className="acc-row-stack">
              <span className="acc-row-label">Password</span>
              <span className="acc-row-value">
                Change your password. Your other devices will be signed out.
              </span>
            </div>
            <Button
              type="button"
              variant="default"
              size="md"
              onClick={vm.actions.openPasswordForm}
              data-testid="account-password-open"
            >
              Change password
            </Button>
          </div>
        )}
      </section>

      <section className="acc-section" data-testid="account-section-function-role">
        <SectionHead marker="04" title="Function role" tag="SEC.04 — ROLE" />

        <div className="acc-row" data-testid="account-function-role-row">
          <div className="acc-row-stack">
            <span className="acc-row-label">
              Your role in {vm.activeOrgName ?? '—'}
            </span>
            <span
              className="acc-row-value"
              data-testid="account-function-role-value"
            >
              {vm.functionRoleLabel ?? '—'}
            </span>
          </div>
          <ButtonRouterLink
            to="/members"
            variant="ghost"
            size="sm"
            data-testid="account-function-role-link"
          >
            Open Members →
          </ButtonRouterLink>
        </div>
        <p className="acc-help">
          Your function role is set by an Owner or Admin of the org. To change
          it, ask an Admin in the Members page.
        </p>
      </section>

      <section className="acc-section" data-testid="account-section-sessions">
        <SectionHead marker="05" title="Sessions" tag="SEC.05 — SESSIONS" />

        <div className="acc-row" data-testid="account-sessions-row">
          <div className="acc-row-stack">
            <span className="acc-row-label">Other devices</span>
            <span className="acc-row-value">
              Signs out every other browser, tab and Penrose IDE instance.
              Your current session stays signed in.
            </span>
          </div>
          <Button
            type="button"
            variant="default"
            size="md"
            onClick={() => void vm.actions.signOutOthers()}
            disabled={vm.sessionsStatus === 'submitting'}
            data-testid="account-sessions-signout"
          >
            {vm.sessionsStatus === 'submitting'
              ? 'Signing out…'
              : 'Sign out everywhere else'}
          </Button>
        </div>
        {vm.sessionsError ? (
          <p className="acc-help" data-testid="account-sessions-error">
            {vm.sessionsError}
          </p>
        ) : null}
      </section>

      <section className="acc-section acc-section-danger" data-testid="account-section-danger">
        <SectionHead marker="06" title="Danger zone" tag="SEC.06 — DANGER" />

        {vm.soleOwnerOrgs.length > 0 ? (
          <>
            <div className="acc-row" data-testid="account-danger-row">
              <div className="acc-row-stack">
                <span className="acc-row-label">Delete account</span>
                <span
                  className="acc-row-value"
                  data-testid="account-danger-refusal"
                >
                  You're the sole Owner of {formatOrgNames(vm.soleOwnerOrgs.map((o) => o.name))}. Transfer ownership or delete the org before deleting your account.
                </span>
              </div>
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled
                data-testid="account-danger-open"
              >
                Delete account…
              </Button>
            </div>
            <div className="acc-actions">
              <ButtonRouterLink
                to="/members"
                variant="ghost"
                size="sm"
                data-testid="account-danger-members-link"
              >
                Open Members →
              </ButtonRouterLink>
            </div>
          </>
        ) : vm.deleteFormOpen ? (
          <form
            className="acc-form"
            onSubmit={handleDeleteSubmit}
            noValidate
            data-testid="account-danger-form"
          >
            <p className="acc-row-value">
              Type your handle to confirm:{' '}
              <strong data-testid="account-danger-handle">{handleString}</strong>
            </p>
            <TextField
              id="acc-delete-confirm"
              label="Handle"
              type="text"
              autoComplete="off"
              value={vm.deleteConfirmHandle}
              onChange={(e) =>
                vm.actions.setDeleteConfirmHandle(e.currentTarget.value)
              }
              helpText="Must match exactly."
              errorText={vm.deleteError ?? undefined}
              disabled={vm.deleteStatus === 'submitting'}
            />
            <div className="acc-actions">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={
                  !vm.deleteHandleMatches || vm.deleteStatus === 'submitting'
                }
                data-testid="account-danger-submit"
              >
                {vm.deleteStatus === 'submitting'
                  ? 'Deleting…'
                  : 'Delete account permanently'}
              </Button>
              <Button
                type="button"
                variant="default"
                size="md"
                onClick={vm.actions.cancelDeleteForm}
                data-testid="account-danger-cancel"
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="acc-row" data-testid="account-danger-row">
            <div className="acc-row-stack">
              <span className="acc-row-label">Delete account</span>
              <span className="acc-row-value">
                Permanently deletes your account and all your data.
              </span>
            </div>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={vm.actions.openDeleteForm}
              data-testid="account-danger-open"
            >
              Delete account…
            </Button>
          </div>
        )}
      </section>

      {vm.profileSavedAt ? (
        <Toast title="Profile updated" onDismiss={vm.actions.clearSavedToast}>
          Your handle preview reflects the latest values.
        </Toast>
      ) : null}

      {vm.emailSentTo ? (
        <Toast
          title="Confirmation link sent"
          durationMs={6000}
          onDismiss={vm.actions.cancelEmailForm}
        >
          We sent a confirmation link to <strong>{vm.emailSentTo}</strong>. Click the link to switch.
        </Toast>
      ) : null}

      {vm.passwordChangedAt ? (
        <Toast
          title="Password updated"
          durationMs={6000}
          onDismiss={vm.actions.clearPasswordToast}
        >
          Other devices have been signed out.
        </Toast>
      ) : null}

      {vm.sessionsSignedOutAt ? (
        <Toast
          title="Signed out of all other devices."
          durationMs={6000}
          onDismiss={vm.actions.clearSessionsToast}
        >
          The Penrose IDE will drop on its next online check.
        </Toast>
      ) : null}
    </section>
  );
}
